import { useEffect } from "react";
import { useLocation } from "react-router";
import { useStored } from "~/core/hooks";

/** The two halves of the product, and the screens that belong to neither. */
export type Module = "analytics" | "agent";
export type Kind = Module | "common";

/**
 * The paths under a brand that are neither module's.
 *
 * Settings holds the brand's own filing, the account's plan and the Agent's
 * workspace secrets; Tasks is one list with a tab for each side, and the
 * Agent's sidebar links straight into its own. Neither is Analytics', which is
 * where both used to sit — so opening Settings from the Agent flipped the mode
 * switch and replaced the whole left column with Analytics' five stages. That
 * is a mode change nobody asked for, and it is why this is a table of three
 * rather than one "is this the Agent" regex copied into two files.
 */
const COMMON = /^\/w\/[^/]+\/(?:settings|tasks)(?:\/|$)/;
/** Every Agent screen and nothing of Analytics, though both sit under `/w/:brandId`. */
const AGENT = /^\/w\/[^/]+\/agent(?:\/|$)/;

/**
 * Which of the three a path is.
 *
 * Analytics is the fallthrough rather than a match because it has no prefix of
 * its own: it is what a brand's workspace is when you have not gone somewhere
 * else.
 */
export function kindOf(pathname: string): Kind {
	if (COMMON.test(pathname)) return "common";
	return AGENT.test(pathname) ? "agent" : "analytics";
}

/** Where each half of the mode switch goes. */
export function moduleHome(brandId: string, module: Module): string {
	return module === "agent" ? `/w/${brandId}/agent` : `/w/${brandId}`;
}

/**
 * Which module the reader is in, including on the screens that are neither.
 *
 * A common screen keeps the module it was opened from, so Settings moves
 * nobody. That cannot come from the path there, so it is remembered — and a
 * bookmark opened cold has nothing to remember, which is why the fallback is
 * the Agent: it is where the product opens.
 */
export function useModule(): Module {
	const kind = kindOf(useLocation().pathname);
	const [remembered, remember] = useStored<Module>("ondit.module", "agent");
	useEffect(() => {
		if (kind !== "common") remember(kind);
	}, [kind, remember]);
	// The path's own answer wins on the pass that changes it; the effect only
	// has to carry it into the common screens that come after.
	return kind === "common" ? remembered : kind;
}
